import axios from 'axios';

// API base URL: use env, else when on Vercel use Render backend, else localhost
// Set REACT_APP_API_URL in .env (or Vercel env vars) for each environment
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 seconds - Render free tier needs time to wake up
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('genlend_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url: string = error.config?.url || '';
    const isAuthAttempt =
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/forgot-password') ||
      url.includes('/auth/reset-password') ||
      url.includes('/auth/verify');

    // Only treat as a dead session when an authenticated call fails with 401.
    // Do NOT hard-redirect on login failures (those are wrong password, not expired session).
    // Soft-clear storage so the next page load is clean; let React Router / AuthContext
    // handle navigation instead of a full window reload (avoids "stuck loading" loops).
    if (status === 401 && !isAuthAttempt) {
      localStorage.removeItem('genlend_token');
      localStorage.removeItem('genlend_user');
      if (!window.location.pathname.startsWith('/auth')) {
        // Prefer client-side navigation when possible; hard reload only as fallback
        window.location.assign('/auth');
      }
    }
    return Promise.reject(error);
  }
);

// User interfaces
export interface User {
  id: string;
  phone_number: string;
  sa_id_number: string;
  first_name: string;
  last_name: string;
  email?: string;
  is_verified: boolean;
  nsfas_verified: boolean;
  bank_account_number?: string;
  bank_name?: string;
  bank_branch_code?: string;
  account_holder_name?: string;
  created_at: string;
  last_login_at?: string;
  status: string;
  role?: string;
}

export interface RegisterRequest {
  phone_number: string;
  sa_id_number: string;
  first_name: string;
  last_name: string;
  email?: string;
  password: string;
  bank_account_number?: string;
  bank_name?: string;
  bank_branch_code?: string;
  account_holder_name?: string;
}

export interface Payment {
  id: string;
  loanId: string;
  loanNumber?: string;
  userId: string;
  borrowerName: string;
  borrowerEmail?: string;
  borrowerPhone?: string;
  amount: number;
  dueDate: string;
  paidDate?: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded';
  paymentMethod?: 'payfast' | 'manual' | 'debit_order';
  paymentType?: 'disbursement' | 'repayment' | 'admin_fee';
  reference?: string;
  notes?: string;
  failureReason?: string;
  createdAt: string;
  updatedAt: string;
  loanSummary?: {
    totalAmount: number;
    principalAmount: number;
    totalPaid: number;
    outstandingBalance: number;
    loanStatus: string;
    installmentTotal: number;
    installmentCompleted: number;
  };
}

export interface PaymentHistoryEntry {
  id: string;
  payment_id: string;
  status: string;
  status_changed_at: string;
  changed_by?: string;
  notes?: string;
  failure_reason?: string;
}

export interface PaymentStats {
  totalPending: number;
  totalOverdue: number;
  totalPaid: number;
  totalAmount: number;
  overdueAmount: number;
  paidAmount: number;
}

export interface LoginRequest {
  phone_number: string;
  password: string;
}

export interface VerifyPhoneRequest {
  phone_number: string;
  verification_code: string;
}

export interface AuthResponse {
  message: string;
  user: User;
  token: string;
  verification_required?: boolean;
}

// Auth API functions
export const authAPI = {
  // Register new user
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  // Login user
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  // Verify phone number
  verifyPhone: async (data: VerifyPhoneRequest): Promise<{ message: string; user: User; verified: boolean }> => {
    const response = await api.post('/auth/verify-phone', data);
    return response.data;
  },

  // Resend verification code
  resendVerificationCode: async (phone_number: string): Promise<{ message: string; phone_number: string }> => {
    const response = await api.post('/auth/resend-verification', { phone_number });
    return response.data;
  },

  // Get user profile
  getProfile: async (): Promise<{ user: User; nsfas_info?: any }> => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  // Update profile (first/last name, email). Phone number changes require OTP flow.
  updateProfile: async (data: {
    first_name?: string;
    last_name?: string;
    email?: string | null;
  }): Promise<{ message: string; user: User }> => {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },

  // Change password (authenticated)
  changePassword: async (data: {
    current_password: string;
    new_password: string;
  }): Promise<{ message: string; success: boolean }> => {
    const response = await api.put('/auth/change-password', data);
    return response.data;
  },

  // Health check
  healthCheck: async (): Promise<any> => {
    const response = await api.get('/auth/health');
    return response.data;
  },

  // Forgot password - request OTP
  forgotPassword: async (phone_number: string): Promise<{ message: string; phone_number: string }> => {
    const response = await api.post('/auth/forgot-password', { phone_number });
    return response.data;
  },

  // Reset password - verify OTP and set new password
  resetPassword: async (data: { phone_number: string; verification_code: string; new_password: string }): Promise<{ message: string; success: boolean }> => {
    const response = await api.post('/auth/reset-password', data);
    return response.data;
  },
};

export const supportAPI = {
  submitTicket: async (data: {
    category: string;
    subject: string;
    message: string;
  }): Promise<{ success: boolean; data: { reference: string }; message: string }> => {
    const response = await api.post('/support/tickets', data);
    return response.data;
  },
};

// Admin API functions
// Payment API
export const paymentAPI = {
  /**
   * Initiate PayFast payment
   */
  initiatePayFastPayment: async (paymentId: string): Promise<{
    success: boolean;
    data: {
      paymentUrl: string;
      paymentParams: any;
    };
    message: string;
  }> => {
    // Token is handled by interceptor
    const response = await api.post(`/payments/${paymentId}/payfast/initiate`, {});
    return response.data;
  },

  /**
   * Get payment status
   */
  getPaymentStatus: async (paymentId: string): Promise<{
    success: boolean;
    data: {
      paymentId: string;
      paymentReference: string;
      status: string;
      amount: number;
      paymentDate?: string;
      payfastPaymentId?: string;
      loanNumber: string;
    };
    message: string;
  }> => {
    const response = await api.get(`/payments/${paymentId}/status`);
    return response.data;
  },

  /**
   * Initiate Ozow payment (Instant EFT) — reserved for future use; not exposed in UI
   */
  initiateOzowPayment: async (paymentId: string): Promise<{
    success: boolean;
    data: {
      paymentUrl: string;
      paymentParams: any;
    };
    message: string;
  }> => {
    const response = await api.post(`/payments/${paymentId}/ozow/initiate`, {});
    return response.data;
  },

  /**
   * @deprecated Use debicheckAPI.initiateMandate instead. Kept only so
   * older code paths surface a clear compile-time error.
   */
  setupDebitOrder: async (_data: unknown): Promise<never> => {
    throw new Error(
      'setupDebitOrder has been replaced by debicheckAPI.initiateMandate'
    );
  },

  /**
   * Get available payment methods
   */
  getPaymentMethods: async (): Promise<{
    success: boolean;
    data: {
      methods: Array<{
        id: string;
        name: string;
        description: string;
        icon: string;
        available: boolean;
        fees: string;
      }>;
    };
    message: string;
  }> => {
    const response = await api.get('/payments/methods');
    return response.data;
  }
};

// ============================================================================
// DebiCheck (Netcash) — mandate authorisation and lifecycle
// ============================================================================

export type DebiCheckMandateStatus =
  | 'pending'
  | 'authenticated'
  | 'declined'
  | 'expired'
  | 'cancelled'
  | 'error'
  | 'rms_registered';

export interface DebiCheckMandate {
  mandateId: string;
  loanId?: string;
  status: DebiCheckMandateStatus;
  mandateReference?: string | null;
  accountReference?: string;
  collectionAmount: number;
  firstCollectionAmount?: number | null;
  firstCollectionDate?: string | null;
  collectionDayCode?: string;
  bankAccountName?: string;
  bankAccountNumber?: string | null;
  branchCode?: string;
  errorCode?: string | null;
  errorMessage?: string | null;
  authenticatedAt?: string | null;
  declinedAt?: string | null;
  cancelledAt?: string | null;
  lastTracedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  mode?: string;
}

export const debicheckAPI = {
  /**
   * Get integration status/mode (safe to expose — no secrets).
   */
  getStatus: async (): Promise<{
    success: boolean;
    data: {
      mode: 'mock' | 'sandbox' | 'live';
      serviceKeyConfigured: boolean;
      mandateTemplateId: string;
      postbackConfigured: boolean;
      softwareVendorConfigured: boolean;
    };
  }> => {
    const response = await api.get('/debicheck/status');
    return response.data;
  },

  /**
   * Kick off a DebiCheck mandate authentication. Returns 202 immediately;
   * poll getMandateStatus for the terminal result.
   */
  initiateMandate: async (data: {
    loanId: string;
    accountHolder: string;
    bankName: string;
    accountNumber: string;
    branchCode: string;
    accountType: string;
    debitDay: string;
  }): Promise<{
    success: boolean;
    data: {
      mandateId: string;
      status: DebiCheckMandateStatus;
      accountReference: string;
      collectionAmount: number;
      firstCollectionDate: string;
      collectionDayCode: string;
      mode: 'mock' | 'sandbox' | 'live';
    };
    message: string;
  }> => {
    const response = await api.post('/debicheck/mandates/initiate', data);
    return response.data;
  },

  getMandateStatus: async (
    mandateId: string
  ): Promise<{ success: boolean; data: DebiCheckMandate }> => {
    const response = await api.get(`/debicheck/mandates/${mandateId}/status`);
    return response.data;
  },

  traceMandate: async (
    mandateId: string
  ): Promise<{
    success: boolean;
    data: {
      mandateId: string;
      status: DebiCheckMandateStatus;
      detail?: string;
      tracedAt: string;
    };
    message: string;
  }> => {
    const response = await api.post(`/debicheck/mandates/${mandateId}/trace`);
    return response.data;
  },

  cancelMandate: async (
    mandateId: string
  ): Promise<{
    success: boolean;
    data: { mandateId: string; status: 'cancelled'; cancelledAt: string };
    message: string;
  }> => {
    const response = await api.post(`/debicheck/mandates/${mandateId}/cancel`);
    return response.data;
  },

  getMandateForLoan: async (
    loanId: string
  ): Promise<{ success: boolean; data: DebiCheckMandate | null }> => {
    const response = await api.get(`/debicheck/loans/${loanId}/mandate`);
    return response.data;
  },

  // Admin listing / details
  adminListMandates: async (params: {
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{
    success: boolean;
    data: {
      mandates: Array<{
        id: string;
        status: DebiCheckMandateStatus;
        mandateReference: string | null;
        accountReference: string;
        collectionAmount: number;
        firstCollectionDate: string | null;
        collectionDayCode: string | null;
        bankAccountName: string | null;
        branchCode: string | null;
        errorCode: string | null;
        errorMessage: string | null;
        authenticatedAt: string | null;
        declinedAt: string | null;
        cancelledAt: string | null;
        lastTracedAt: string | null;
        createdAt: string;
        updatedAt: string;
        user: {
          id: string;
          firstName: string;
          lastName: string;
          phoneNumber: string;
          email: string | null;
        };
        loan: {
          id: string;
          loanNumber: string;
          totalAmount: number;
          status: string;
        };
      }>;
      pagination: { total: number; limit: number; offset: number };
    };
  }> => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    const response = await api.get(`/admin/debicheck/mandates${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  adminGetMandateDetail: async (
    mandateId: string
  ): Promise<{ success: boolean; data: any }> => {
    const response = await api.get(`/admin/debicheck/mandates/${mandateId}`);
    return response.data;
  },

  adminAuthoriseMandate: async (
    mandateId: string,
    data?: { nupayReference?: string; notes?: string }
  ): Promise<{
    success: boolean;
    data: {
      mandateId: string;
      status: DebiCheckMandateStatus;
      mandateReference?: string;
      authenticatedAt?: string;
      alreadyAuthorised?: boolean;
    };
    message: string;
  }> => {
    const response = await api.post(
      `/admin/debicheck/mandates/${mandateId}/authorise`,
      data || {}
    );
    return response.data;
  },
};

export const adminAPI = {
  // Get dashboard overview metrics
  getDashboardOverview: async (): Promise<{ success: boolean; data: DashboardMetrics; message: string }> => {
    const response = await api.get('/admin/dashboard/overview');
    return response.data;
  },

  // Get applications queue
  getApplicationsQueue: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: { applications: Application[]; pagination: PaginationInfo }; message: string }> => {
    const response = await api.get('/admin/applications', { params });
    return response.data;
  },

  // Get application details
  getApplicationDetails: async (applicationId: string): Promise<{ success: boolean; data: Application; message: string }> => {
    const response = await api.get(`/admin/applications/${applicationId}`);
    return response.data;
  },

  // Process application (approve, reject, request info)
  processApplication: async (applicationId: string, data: ProcessApplicationRequest): Promise<{ success: boolean; data: Application; message: string }> => {
    const response = await api.put(`/admin/applications/${applicationId}/process`, data);
    return response.data;
  },

  // Get users list
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }): Promise<{ success: boolean; data: { users: UserManagement[]; pagination: PaginationInfo }; message: string }> => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  // Update user status
  updateUserStatus: async (userId: string, action: 'suspend' | 'activate' | 'delete'): Promise<{ success: boolean; data: any; message: string }> => {
    const response = await api.put(`/admin/users/${userId}/status`, { action });
    return response.data;
  },

  // Get payment tracking data
  getPaymentTracking: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: { payments: PaymentTracking[]; pagination: PaginationInfo; summary: PaymentSummary }; message: string }> => {
    const response = await api.get('/admin/payments', { params });
    return response.data;
  },

  // Document Verification
  fetchDocumentFile: async (documentId: string): Promise<Blob> => {
    try {
      const response = await api.get(`/admin/documents/${documentId}/file`, {
        responseType: 'blob',
      });
      const type = String(response.headers['content-type'] || '');
      if (type.includes('application/json')) {
        const text = await (response.data as Blob).text();
        const body = JSON.parse(text);
        throw new Error(body.message || 'Could not open this file');
      }
      return response.data as Blob;
    } catch (error: any) {
      const data = error?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        try {
          const body = JSON.parse(text);
          throw new Error(body.message || 'Could not open this file');
        } catch (parseError) {
          if (parseError instanceof SyntaxError) {
            throw error;
          }
          throw parseError;
        }
      }
      throw error;
    }
  },

  getDocumentVerification: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: { documents: DocumentVerification[]; pagination: PaginationInfo; summary: DocumentSummary }; message: string }> => {
    const response = await api.get('/admin/documents', { params });
    return response.data;
  },

  processDocumentVerification: async (documentId: string, data: ProcessDocumentRequest): Promise<{ success: boolean; data: { id: string; status: string; notes?: string; verifiedAt?: string; verifiedBy?: string }; message: string }> => {
    const response = await api.put(`/admin/documents/${documentId}/process`, data);
    return response.data;
  },

  // Reports & Analytics
  getReportsAnalytics: async (): Promise<{ success: boolean; data: ReportsAnalytics; message: string }> => {
    const response = await api.get('/admin/reports');
    return response.data;
  },

  exportReportsData: async (params: {
    format: 'csv' | 'excel' | 'pdf';
    type: 'loans' | 'users' | 'payments' | 'financial';
    dateRange?: string;
  }): Promise<{ success: boolean; data: ExportData; message: string }> => {
    const response = await api.get('/admin/reports/export', { params });
    return response.data;
  },

  // Payment Management
  getPayments: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: { payments: Payment[]; pagination: PaginationInfo }; message: string }> => {
    const response = await api.get('/admin/payments', { params });
    return response.data;
  },

  updatePaymentStatus: async (paymentId: string, data: {
    status: 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded';
    paymentMethod?: string;
    reference?: string;
    notes?: string;
    failureReason?: string;
  }): Promise<{ success: boolean; data: Payment; message: string }> => {
    const response = await api.put(`/admin/payments/${paymentId}/status`, data);
    return response.data;
  },

  // Get payment history for a loan
  getLoanPaymentHistory: async (loanId: string): Promise<{ success: boolean; data: { loanId: string; history: PaymentHistoryEntry[] }; message: string }> => {
    const response = await api.get(`/admin/loans/${loanId}/payment-history`);
    return response.data;
  },

  // Retry failed payment
  retryFailedPayment: async (paymentId: string, failureReason?: string): Promise<{ success: boolean; data: { paymentId: string; shouldRetry: boolean; retryCount: number }; message: string }> => {
    const response = await api.post(`/admin/payments/${paymentId}/retry`, { failureReason });
    return response.data;
  },

  // Get payments needing retry
  getPaymentsForRetry: async (): Promise<{ success: boolean; data: { payments: Payment[] }; message: string }> => {
    const response = await api.get('/admin/payments/retry');
    return response.data;
  },

  // Reset payment to pending (for testing)
  resetPaymentToPending: async (paymentId: string): Promise<{ success: boolean; message: string; data?: any }> => {
    const response = await api.post(`/admin/payments/${paymentId}/reset-to-pending`);
    return response.data;
  },

  // Send payment reminder (SMS and/or Email)
  sendPaymentReminder: async (paymentId: string, type: 'sms' | 'email' | 'both'): Promise<{ success: boolean; message: string; data?: any }> => {
    const response = await api.post(`/admin/payments/${paymentId}/send-reminder`, { type });
    return response.data;
  },

  // Disburse loan (pay the student)
  disburseLoan: async (loanId: string, data?: { disbursementMethod?: string; disbursementReference?: string; notes?: string }): Promise<{ success: boolean; message: string; data?: any }> => {
    const response = await api.post(`/admin/loans/${loanId}/disburse`, data || {});
    return response.data;
  },
};

// Loan Application API
export const loanAPI = {
  // Submit loan application with file uploads
  submitApplication: async (data: LoanApplicationRequest): Promise<{ success: boolean; data: any; message: string }> => {
    // Use FormData to handle file uploads
    const formData = new FormData();
    
    // Add all text fields
    formData.append('loanAmount', data.loanAmount.toString());
    formData.append('firstName', data.firstName);
    formData.append('lastName', data.lastName);
    if (data.email) formData.append('email', data.email);
    formData.append('phoneNumber', data.phoneNumber);
    formData.append('saIdNumber', data.saIdNumber);
    formData.append('bankName', data.bankName);
    formData.append('accountNumber', data.accountNumber);
    formData.append('accountType', data.accountType);
    formData.append('branchCode', data.branchCode);
    if (data.institutionName) formData.append('institutionName', data.institutionName);
    if (data.studentNumber) formData.append('studentNumber', data.studentNumber);
    if (data.studyYear) formData.append('studyYear', data.studyYear);
    if (data.nsfasMonthlyAllowance) formData.append('nsfasMonthlyAllowance', data.nsfasMonthlyAllowance);
    if (data.hasOtherCredit) formData.append('hasOtherCredit', data.hasOtherCredit);
    if (data.otherCreditMonthly) formData.append('otherCreditMonthly', data.otherCreditMonthly);
    formData.append('termsAccepted', data.termsAccepted.toString());
    formData.append('privacyAccepted', data.privacyAccepted.toString());
    formData.append('creditCheckConsent', data.creditCheckConsent.toString());
    
    // Add file uploads
    if (data.nsfasStatement) {
      formData.append('nsfasStatement', data.nsfasStatement);
    }
    if (data.bankStatement) {
      formData.append('bankStatement', data.bankStatement);
    }
    if (data.studentCard) {
      formData.append('studentCard', data.studentCard);
    }
    
    // Send as multipart/form-data
    const response = await api.post('/loans/apply', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  settleLoan: async (loanId: string): Promise<{ success: boolean; data: { paymentId: string; amount: number } }> => {
    const response = await api.post(`/loans/${loanId}/settle`);
    return response.data;
  },

  // Get user's loan applications
  getUserApplications: async (): Promise<{ success: boolean; data: any[]; message: string }> => {
    const response = await api.get('/loans/applications');
    return response.data;
  },

  // Get loan application details
  getApplicationDetails: async (loanId: string): Promise<{ success: boolean; data: any; message: string }> => {
    const response = await api.get(`/loans/applications/${loanId}`);
    return response.data;
  },

  // Check loan eligibility (can the student apply for a new loan?)
  checkEligibility: async (): Promise<{
    success: boolean;
    data: {
      eligible: boolean;
      reasons: string[];
      activeLoans: number;
      openLoans?: Array<{
        id: string;
        loanNumber: string;
        status: string;
        outstanding: number;
      }>;
      hasPendingApplication: boolean;
      totalOutstanding: number;
      maxAllowedAmount: number;
    };
  }> => {
    const response = await api.get('/loans/eligibility');
    return response.data;
  },

  // Get student's payments
  getStudentPayments: async (): Promise<{
    success: boolean;
    data: {
      payments: Array<{
        id: string;
        loanId: string;
        loanNumber: string;
        amount: number;
        dueDate?: string;
        paidDate?: string;
        status: string;
        paymentMethod?: string;
        reference?: string;
        createdAt: string;
        updatedAt: string;
      }>;
      loans: Array<{
        id: string;
        loanNumber: string;
        principalAmount: number;
        interestRate: number;
        interestAmount: number;
        adminFee: number;
        totalAmount: number;
        totalPaid: number;
        outstandingBalance: number;
        status: string;
        rejectionReason?: string;
        repaymentDate?: string;
        applicationDate?: string;
        createdAt: string;
      }>;
      summary: {
        totalPending: number;
        totalCompleted: number;
        totalFailed: number;
        pendingAmount: number;
        completedAmount: number;
        totalPayments: number;
      };
    };
    message: string;
  }> => {
    const response = await api.get('/loans/payments');
    return response.data;
  },
};

// Loan Application Request Interface
export interface LoanApplicationRequest {
  loanAmount: number;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber: string;
  saIdNumber: string;
  bankName: string;
  accountNumber: string;
  accountType: 'savings' | 'cheque';
  branchCode: string;
  institutionName: string;
  studentNumber: string;
  studyYear?: string;
  nsfasMonthlyAllowance?: string;
  hasOtherCredit?: 'yes' | 'no' | '';
  otherCreditMonthly?: string;
  nsfasStatement?: File;
  bankStatement?: File;
  studentCard?: File;
  termsAccepted: boolean;
  privacyAccepted: boolean;
  creditCheckConsent: boolean;
};

// Loan calculation interface (for future use)
export interface LoanCalculation {
  principal_amount: number;
  interest_rate: number;
  interest_amount: number;
  admin_fee: number;
  total_amount: number;
  repayment_amount: number;
  next_nsfas_payment_date: string;
}

// Admin interfaces
export interface DashboardActivity {
  id: string;
  type: 'application' | 'payment' | 'verification';
  view: 'applications' | 'payments' | 'documents';
  description: string;
  status: 'success' | 'warning' | 'error' | 'info';
  occurredAt: string;
}

export interface DashboardMetrics {
  totalApplications: number;
  pendingApplications: number;
  approvedApplications: number;
  activeLoans?: number;
  rejectedApplications: number;
  totalUsers: number;
  activeUsers: number;
  totalLoanAmount: number;
  outstandingAmount?: number;
  totalRepaymentAmount: number;
  averageLoanAmount: number;
  approvalRate: number;
  repaymentRate?: number;
  pendingDocuments?: number;
  failedPayments?: number;
  pendingMandates?: number;
  recentActivity?: DashboardActivity[];
  applicationVolume?: Array<{ label: string; date: string; count: number }>;
  repaymentStatus?: {
    repaid: number;
    overdue: number;
    defaulted: number;
  };
}

export interface RiskAssessment {
  overallScore: number;
  riskLevel: 'low' | 'medium' | 'high' | 'very_high';
  breakdown: {
    nsfasVerification: number;
    documentCompleteness: number;
    loanAmount: number;
    paymentHistory: number;
    accountHistory: number;
    applicationPattern: number;
  };
  recommendations: string[];
}

export interface Application {
  id: string;
  applicantName: string;
  phone: string;
  email: string;
  amount: number;
  totalRepayment: number;
  submittedAt: string;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'requires_info';
  institution: string;
  studentNumber: string;
  saIdNumber?: string;
  riskScore: number;
  documentsComplete: boolean;
  nsfasVerified: boolean;
  reviewedBy?: string;
  reviewedAt?: string;
  comments?: string;
  rejectionReason?: string;
  lastUpdated: string;
  studyYear?: string;
  nsfasMonthlyAllowance?: number;
  hasOtherCredit?: boolean | null;
  otherCreditMonthly?: number;
  riskAssessment?: RiskAssessment;
  bankDetails?: {
    bankName?: string;
    accountNumber?: string;
    branchCode?: string;
    accountHolder?: string;
  };
}

export interface UserManagement {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  institution: string;
  studentNumber: string;
  nsfasVerified: boolean;
  status: 'active' | 'suspended' | 'deleted';
  registeredAt: string;
  lastLoginAt: string;
  totalLoans: number;
  totalBorrowed: number;
  totalRepaid: number;
}

export interface PaymentTracking {
  id: string;
  loanId: string;
  borrowerName: string;
  amount: number;
  dueDate: string;
  status: 'pending' | 'paid' | 'overdue';
  paymentMethod: string;
  createdAt: string;
  loanAmount: number;
  totalRepayment: number;
  interestPaid: number;
  principalPaid: number;
  outstandingBalance: number;
  paymentHistory: PaymentHistory[];
}

export interface PaymentHistory {
  id: string;
  amount: number;
  date: string;
  status: 'scheduled' | 'completed' | 'failed';
  type: 'principal' | 'interest' | 'full_payment';
  failureReason?: string;
}

export interface PaymentSummary {
  totalPayments: number;
  pendingPayments: number;
  paidPayments: number;
  overduePayments: number;
  totalOutstanding: number;
  totalCollected: number;
}

export interface DocumentVerification {
  id: string;
  applicationId: string;
  borrowerName: string;
  documentType: string;
  fileName: string;
  fileSize: number;
  mimeType?: string;
  uploadedAt: string;
  status: 'pending_review' | 'verified' | 'rejected';
  reviewerId?: string;
  reviewedAt?: string;
  verificationNotes?: string;
  documentUrl: string;
  metadata: {
    institution: string;
    studentNumber: string;
    academicYear: string;
    semester: string;
  };
}

export interface DocumentSummary {
  totalDocuments: number;
  pendingReview: number;
  verified: number;
  rejected: number;
  averageReviewTime: number;
  verificationRate: number;
}

export interface ProcessDocumentRequest {
  action: 'verify' | 'reject';
  notes?: string;
}

export interface ReportsAnalytics {
  overview: {
    totalRevenue: number;
    totalLoans: number;
    averageLoanAmount: number;
    repaymentRate: number;
    defaultRate: number;
    monthlyGrowth: number;
    quarterlyGrowth: number;
  };
  loanMetrics: {
    totalDisbursed: number;
    totalRepaid: number;
    outstandingBalance: number;
    averageRepaymentTime: number;
    loansByAmount: Array<{
      range: string;
      count: number;
      percentage: number;
    }>;
    loansByInstitution: Array<{
      institution: string;
      count: number;
      amount: number;
    }>;
  };
  userMetrics: {
    totalUsers: number;
    activeUsers: number;
    newUsersThisMonth: number;
    userRetentionRate: number;
    averageLoansPerUser: number;
    usersByInstitution: Array<{
      institution: string;
      count: number;
    }>;
  };
  financialMetrics: {
    monthlyRevenue: Array<{
      month: string;
      revenue: number;
      loans: number;
    }>;
    profitMargin: number;
    operationalCosts: number;
    netProfit: number;
  };
  riskMetrics: {
    averageRiskScore: number;
    riskDistribution: Array<{
      range: string;
      count: number;
      percentage: number;
    }>;
    defaultByRisk: Array<{
      riskLevel: string;
      defaultRate: number;
    }>;
  };
  trends: {
    applicationTrends: Array<{
      period: string;
      applications: number;
      approvals: number;
    }>;
    seasonalPatterns: {
      peakMonths: string[];
      lowMonths: string[];
      averageMonthlyApplications: number;
    };
  };
}

export interface ExportData {
  filename: string;
  data: string;
  size: number;
  generatedAt: string;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface ProcessApplicationRequest {
  action: 'approve' | 'reject' | 'request_info';
  comments?: string;
  rejectionReason?: string;
}

// Utility functions
export const formatPhoneNumber = (phone: string): string => {
  // Convert 0XXXXXXXXX to +27XXXXXXXXX
  if (phone.startsWith('0')) {
    return '+27' + phone.slice(1);
  }
  return phone;
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
  }).format(amount);
};

export default api;
